import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Input from "../../components/input";
import FIlterSelect from "../../components/filter-select";
import Table from "../../components/table";
import {
  capitalizeFirstLetter,
  formatDate,
  getPermissionColor,
  getPermissionTextColor,
  getStatusColor,
  getStatusTextColor,
} from "../../utilz/function";
import EditRole from "./user-modal/edit-role";

interface Permission {
  resource: string;
  actions: string[];
  _id: string;
}

interface RolesData {
  _id: string;
  createdAt: string;
  updatedAt: string;
  permissions: Permission[];
  name: string;
  isActive?: string;
  [key: string]: string | undefined | Permission[];
}

interface RolesTableProps {
  data: RolesData[];
  selectedStatus?: string;
  handleStatusChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

const PermissionTable: React.FC<RolesTableProps> = ({
  data,
  selectedStatus,
}) => {
  const navigate = useNavigate();
  const [filteredData, setFilteredData] = useState<RolesData[]>([]);
  const [editRoleModal, setEditRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RolesData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoleStatus, setSelectedRoleStatus] = useState("");

  const columns = [
    { header: "Role Name", accessor: "name" },
    { header: "Permissions", accessor: "permissions" },
    { header: "Created At", accessor: "createdAt" },
    { header: "Last Updated", accessor: "updatedAt" },
    { header: "Status", accessor: "status" },
    { header: "Actions", accessor: "_id" },
  ];

  useEffect(() => {
    const filtered = data.filter((role) => {
      const statusMatch = !selectedStatus || role.isActive === selectedStatus;

      const searchMatch =
        !searchQuery ||
        role.name.toLowerCase().includes(searchQuery.toLowerCase());

      const roleStatusMatch =
        !selectedRoleStatus || role.isActive === selectedRoleStatus;

      return statusMatch && searchMatch && roleStatusMatch;
    });
    setFilteredData(filtered);
  }, [data, searchQuery, selectedStatus, selectedRoleStatus]);


  function closeEditRoleModal() {
    setEditRoleModal(false);
    setSelectedRole(null);
  }

  function openEditRoleModal(id: string) {
    const roleToEdit = data.find(role => role._id === id) || null;
    setSelectedRole(roleToEdit);
    setEditRoleModal(true);
  }

  const handleRoleClick = (id: string) => {
    openEditRoleModal(id);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleStatusFilterChange = (selectedValue: string) => {
    setSelectedRoleStatus(selectedValue);
  };

  const combinedData = filteredData.map((role) => ({
    ...role,
    permissions: (
      <div className="flex flex-wrap gap-2">
        {role.permissions?.map((permission) => (
          <span
            key={permission._id}
            className="font-normal rounded-full p-2"
            style={{
              backgroundColor: getPermissionColor(permission.resource),
              color: getPermissionTextColor(permission.resource),
            }}
          >
            {capitalizeFirstLetter(permission.resource)}
          </span>
        ))}
        {!role.permissions?.length && "No Permissions"}
      </div>
    ),
    status: (
      <div
        className="flex flex-wrap gap-2"
        style={{
          backgroundColor: getStatusColor(
            role?.isActive === "true" ? "Active" : "Inactive"
          ),
          color: getStatusTextColor(
            role?.isActive === "true" ? "Active" : "Inactive"
          ),
        }}
      >
        {role?.isActive === "true" ? "Active" : "Inactive"}
      </div>
    ),

    createdAt: formatDate(role.createdAt),
    updatedAt: formatDate(role.updatedAt),
  }));

  return (
    <>
      <div className="py-3 bg-white rounded-3xl shadow border border-[#C9C9C9] mt-10 pt-5">
        <div className="md:flex justify-between px-5">
          <p className="text-[20px] font-bold">Roles</p>
          <div className="md:flex gap-2 items-center md:mt-0 mt-2">
            <Input
              name="search"
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full"
              placeholder="Search by role name"
              icon="searchIcon"
            />

            <FIlterSelect
              name="statusFilter"
              options={[
                { value: "", label: "Status" },
                { value: "true", label: "Active" },
                { value: "false", label: "Inactive" },
              ]}
              className="mt-2 md:-mt-2 w-full md:w-[50%]"
              icon="filterIcon"
              onChange={handleStatusFilterChange}
              value={selectedRoleStatus}
            />
          </div>
        </div>

        {combinedData.length > 0 ? (
          <Table
            columns={columns}
            data={combinedData}
            selectedUserId={selectedRole?._id || null}
            onEditClick={handleRoleClick}
          />
        ) : (
          <div className="opacity-80 mt-10 font-bold flex justify-center mx-auto">
            {data.length === 0 ? "No roles found" : "No matching roles"}
          </div>
        )}
      </div>
      <EditRole
        onClose={closeEditRoleModal}
        isVisible={editRoleModal}
        roleData={selectedRole}
      />
    </>
  );
};

export default PermissionTable;
